import { useEffect, useState } from 'react';
import { ApiRequest } from '@/utils/axios';
import { useSubscription } from '@/utils/hooks/use-subscription';
import { itemsValue } from '@/utils/itemInfo';
import { ICoinflipGame } from '@/utils/types/ICoinflipGame';
import { ICaseBattleGame } from '@/utils/types/ICaseBattle';

interface JackpotStateRes {
    state?: { value?: number };
}
interface CoinflipListRes {
    games?: ICoinflipGame[];
}

export function useNavGameStats() {
    const [jackpotValue, setJackpotValue] = useState(0);
    const [coinflipTotal, setCoinflipTotal] = useState(0);
    const [casebattleTotal, setCasebattleTotal] = useState(0);

    async function refreshJackpot() {
        try {
            const res = await ApiRequest.get<{ response: JackpotStateRes }>('/jackpot');
            setJackpotValue(res?.data?.response?.state?.value ?? 0);
        } catch {
            /* ignore */
        }
    }

    async function refreshCoinflip() {
        try {
            const res = await ApiRequest.get<{ response: CoinflipListRes }>('/coinflip');
            const games = res?.data?.response?.games ?? [];
            let total = 0;
            for (const g of games) {
                total += itemsValue([...(g.firstItems ?? []), ...(g.secondItems ?? [])]);
            }
            setCoinflipTotal(total);
        } catch {
            /* ignore */
        }
    }

    async function refreshCasebattle() {
        try {
            // /casebattle/list returns the active games array directly under
            // `response` (not wrapped like coinflip). Total in-play money is
            // priceUsd × actually-joined participants summed across games.
            const res = await ApiRequest.get<{ response: ICaseBattleGame[] }>(
                '/casebattle/list'
            );
            const games = res?.data?.response ?? [];
            let total = 0;
            for (const g of games) {
                if (g.status === 'FINISHED') continue;
                const joined = (g.teams ?? []).reduce(
                    (n, t) => n + (t.participants?.length ?? 0),
                    0
                );
                total += (g.priceUsd ?? 0) * joined;
            }
            setCasebattleTotal(total);
        } catch {
            /* ignore */
        }
    }

    useEffect(() => {
        refreshJackpot();
        refreshCoinflip();
        refreshCasebattle();
    }, []);

    useSubscription('jackpotValue', (v: number) => setJackpotValue(v ?? 0)).then();
    useSubscription('jackpotBet', (data: { state?: { value?: number } }) => {
        if (typeof data?.state?.value === 'number') setJackpotValue(data.state.value);
    }).then();
    useSubscription('jackpotCreated', () => setJackpotValue(0)).then();
    useSubscription('jackpotFinished', () => setJackpotValue(0)).then();

    useSubscription('coinflipCreated', () => refreshCoinflip()).then();
    useSubscription('coinflipJoined', () => refreshCoinflip()).then();
    useSubscription('coinflipDeleted', () => refreshCoinflip()).then();
    useSubscription('coinflipFinished', () => refreshCoinflip()).then();

    useSubscription('casebattleCreated', () => refreshCasebattle()).then();
    useSubscription('casebattleJoined', () => refreshCasebattle()).then();
    useSubscription('casebattleStarted', () => refreshCasebattle()).then();
    useSubscription('casebattleFinished', () => refreshCasebattle()).then();

    return { jackpotValue, coinflipTotal, casebattleTotal };
}

export function formatNavMoney(v: number): string {
    if (!isFinite(v) || v <= 0) return '$0';
    if (v >= 10000) return '$' + (v / 1000).toFixed(1) + 'k';
    if (v >= 1000) return '$' + Math.round(v).toLocaleString('en-US');
    if (v >= 100) return '$' + Math.round(v);
    if (v >= 10) return '$' + v.toFixed(1);
    return '$' + v.toFixed(2);
}
